import React, { useContext, useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Input,
  Label,
  FormGroup,
  Table,
} from "reactstrap";
import * as XLSX from "xlsx";
import { useSearchParams } from "react-router-dom";

import WhatsappMessagesReportPage from "./WhatsappMessagesReportPage";
import OrderService from "../../service/order.service";
import TableService from "../../service/table.service";
import { ApiContext } from "../../contexts/ApiContext";
import OrderDetailsModal from "../Orders/OrderDetailsModal";
import { getOrderSourceLabel } from "../../utils/orderSource";

const STATUS_LABELS = {
  PENDING: "Pendente",
  CONFIRMED: "Confirmado",
  PREPARING: "Preparando",
  READY_FOR_DELIVERY: "Pronto p/ entrega",
  OUT_FOR_DELIVERY: "Saiu p/ entrega",
  DELIVERED: "Entregue",
  CANCELLED: "Cancelado",
};

const TYPE_LABELS = {
  DELIVERY: "Delivery",
  PICKUP: "Retirada",
  DINE_IN: "No local",
};

const PAYMENT_LABELS = {
  CASH: "Dinheiro",
  CARD: "Cartão",
  PIX: "Pix",
  ONLINE: "Pagamento virtual",
};

const brl = (cents) =>
  ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const fmtDate = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "");

const daysAgo = (n) => {
  const d = new Date(Date.now() - n * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

const toIso = (date, endOfDay) =>
  date ? new Date(`${date}T${endOfDay ? "23:59:59.999" : "00:00:00"}`).toISOString() : undefined;

const PAGE_SIZE = 20;

const TABS = [
  { key: "pedidos", label: "Pedidos" },
  { key: "mensagens", label: "Mensagens enviadas" },
];

const ReportsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "mensagens" ? "mensagens" : "pedidos";

  return (
    <div className="content">
      <style>{`
        .reports-tabs { display: inline-flex; flex-wrap: wrap; gap: 8px; margin-bottom: 1.25rem; }
        .reports-tab {
          appearance: none;
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #64748b;
          font-weight: 600;
          font-size: 13px;
          line-height: 1;
          padding: 10px 18px;
          border-radius: 999px;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .reports-tab:hover { color: #7c3aed; border-color: rgba(124, 58, 237, 0.35); background: rgba(124, 58, 237, 0.04); }
        .reports-tab.is-active {
          color: #fff;
          border-color: transparent;
          background: linear-gradient(135deg, #7c3aed 0%, #2563eb 100%);
          box-shadow: 0 6px 16px rgba(124, 58, 237, 0.28);
        }
      `}</style>
      <div className="reports-tabs" role="tablist" aria-label="Relatórios">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={`reports-tab${tab === t.key ? " is-active" : ""}`}
            onClick={() => setSearchParams(t.key === "pedidos" ? {} : { tab: t.key })}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "mensagens" ? <WhatsappMessagesReportPage /> : <OrdersReport />}
    </div>
  );
};

const OrdersReport = () => {
  const { account } = useContext(ApiContext);
  const accountId = account?.id;

  const [filters, setFilters] = useState({
    from: daysAgo(7),
    to: today(),
    status: "",
    type: "",
    payment: "",
    tableId: "",
    search: "",
  });
  const [tables, setTables] = useState([]);
  const [result, setResult] = useState({ data: [], total: 0, totalCents: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (!accountId) return;
    TableService.list(accountId)
      .then((list) => setTables(list || []))
      .catch(() => setTables([]));
  }, [accountId]);

  const buildParams = (overrides = {}) => ({
    accountId,
    from: toIso(filters.from, false),
    to: toIso(filters.to, true),
    status: filters.status || undefined,
    type: filters.type || undefined,
    payment: filters.payment || undefined,
    tableId: filters.tableId || undefined,
    search: filters.search || undefined,
    ...overrides,
  });

  const load = async (p = 1) => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await OrderService.getReport(buildParams({ page: p, size: PAGE_SIZE }));
      setResult(res || { data: [], total: 0, totalCents: 0, totalPages: 1 });
      setPage(res?.page || p);
    } catch (err) {
      console.error("Falha ao carregar relatório", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accountId) load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const onFilterChange = (field) => (e) =>
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSearch = () => load(1);

  const handleClear = () => {
    setFilters({ from: daysAgo(7), to: today(), status: "", type: "", payment: "", tableId: "", search: "" });
    setTimeout(() => load(1), 0);
  };

  const handleStatusUpdated = (id, status) => {
    setResult((prev) => ({
      ...prev,
      data: prev.data.map((o) => (o.id === id ? { ...o, status } : o)),
    }));
  };

  const handleExport = async () => {
    if (!accountId) return;
    setExporting(true);
    try {
      const res = await OrderService.getReport(buildParams({ page: 1, size: 10000 }));
      const rows = (res?.data || []).map((o) => ({
        Pedido: o.id,
        Data: fmtDate(o.createdAt),
        Cliente: o.customerName,
        'Telefone/WhatsApp': o.customerPhone,
        Status: STATUS_LABELS[o.status] || o.status,
        Tipo: TYPE_LABELS[o.type] || o.type,
        Origem: getOrderSourceLabel(o),
        Pagamento: PAYMENT_LABELS[o.payment] || o.payment,
        Mesa: o.tableName || "",
        Itens: (o.items || []).map((i) => `${i.quantity}x ${i.name}`).join("; "),
        Subtotal: (o.subtotalCents || 0) / 100,
        Entrega: (o.deliveryCents || 0) / 100,
        Total: (o.totalCents || 0) / 100,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Pedidos");
      XLSX.writeFile(wb, `relatorio-pedidos-${today()}.xlsx`);
    } catch (err) {
      console.error("Falha ao exportar", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Row>
        <Col xs="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Relatório de pedidos</CardTitle>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="3" sm="6">
                  <FormGroup>
                    <Label>De</Label>
                    <Input type="date" value={filters.from} onChange={onFilterChange("from")} />
                  </FormGroup>
                </Col>
                <Col md="3" sm="6">
                  <FormGroup>
                    <Label>Até</Label>
                    <Input type="date" value={filters.to} onChange={onFilterChange("to")} />
                  </FormGroup>
                </Col>
                <Col md="3" sm="6">
                  <FormGroup>
                    <Label>Status</Label>
                    <Input type="select" value={filters.status} onChange={onFilterChange("status")}>
                      <option value="">Todos</option>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="3" sm="6">
                  <FormGroup>
                    <Label>Tipo</Label>
                    <Input type="select" value={filters.type} onChange={onFilterChange("type")}>
                      <option value="">Todos</option>
                      {Object.entries(TYPE_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="3" sm="6">
                  <FormGroup>
                    <Label>Pagamento</Label>
                    <Input type="select" value={filters.payment} onChange={onFilterChange("payment")}>
                      <option value="">Todos</option>
                      {Object.entries(PAYMENT_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                {tables.length > 0 && (
                  <Col md="3" sm="6">
                    <FormGroup>
                      <Label>Mesa</Label>
                      <Input type="select" value={filters.tableId} onChange={onFilterChange("tableId")}>
                        <option value="">Todas</option>
                        {tables.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                )}
                <Col md="3" sm="6">
                  <FormGroup>
                    <Label>Cliente / Telefone/WhatsApp</Label>
                    <Input
                      type="text"
                      placeholder="Buscar..."
                      value={filters.search}
                      onChange={onFilterChange("search")}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    />
                  </FormGroup>
                </Col>
                <Col md="3" sm="6" className="d-flex align-items-end">
                  <FormGroup className="w-100">
                    <Button color="primary" className="mr-2" onClick={handleSearch} disabled={loading}>
                      Filtrar
                    </Button>
                    <Button color="secondary" onClick={handleClear} disabled={loading}>
                      Limpar
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col xs="12">
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col sm="6">
                  <CardTitle tag="h4">
                    {result.total} pedido(s) — {brl(result.totalCents)}
                  </CardTitle>
                </Col>
                <Col sm="6" className="text-right">
                  <Button color="success" onClick={handleExport} disabled={exporting || !result.total}>
                    <i className="tim-icons icon-cloud-download-93 mr-1" />
                    {exporting ? "Exportando..." : "Exportar XLSX"}
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Table className="tablesorter" responsive>
                <thead className="text-primary">
                  <tr>
                    <th>#</th>
                    <th>Data</th>
                    <th>Cliente</th>
                    <th>Telefone/WhatsApp</th>
                    <th>Status</th>
                    <th>Tipo</th>
                    <th>Origem</th>
                    <th>Pagamento</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted">
                        Carregando...
                      </td>
                    </tr>
                  ) : result.data.length ? (
                    result.data.map((o) => (
                      <tr
                        key={o.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedOrder(o)}
                      >
                        <td>{o.id}</td>
                        <td>{fmtDate(o.createdAt)}</td>
                        <td>{o.customerName}</td>
                        <td>{o.customerPhone}</td>
                        <td>{STATUS_LABELS[o.status] || o.status}</td>
                        <td>{TYPE_LABELS[o.type] || o.type}</td>
                        <td>{getOrderSourceLabel(o)}</td>
                        <td>{PAYMENT_LABELS[o.payment] || o.payment}</td>
                        <td className="text-right">{brl(o.totalCents)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center text-muted">
                        Nenhum pedido encontrado
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {result.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <Button color="info" size="sm" disabled={page <= 1 || loading} onClick={() => load(page - 1)}>
                    Anterior
                  </Button>
                  <span className="text-muted">
                    Página {page} de {result.totalPages}
                  </span>
                  <Button
                    color="info"
                    size="sm"
                    disabled={page >= result.totalPages || loading}
                    onClick={() => load(page + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <OrderDetailsModal
        isOpen={!!selectedOrder}
        toggle={() => setSelectedOrder(null)}
        order={selectedOrder}
        onUpdated={handleStatusUpdated}
      />
    </>
  );
};

export default ReportsPage;
